// APIConnector.jsx
import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { ScoopButton, ScoopTextField, ScoopSpinner, ScoopText, ScoopDialog } from '../../../ui'
import styles from './APIConnector.module.scss'
import { Server } from '../../../../api/Server'
import { APIFields } from './APIFields'
import { DataGrid } from '@mui/x-data-grid'

import { displayPorperProductNameInDialog, displayProperProductButton } from './APIUIOverrides'
import { EVENTS, trackEvent } from '../../../../events'
import useDrivePicker from 'react-google-drive-picker'
import { SOURCES_OPTIONS } from '../../Source/SourcesOptions'
import SOC from '../../../../assets/appconnect/SOC.png'
import {
    POLLING_CHECK_ACTIONS,
    useProcessingPolling,
} from '../../../../api/hooks/processing/useProcessingPolling'
import { GooglePickerDialog } from './GooglePickerDialog/GooglePickerDialog'
import { SetupChoice } from './SetupChoice/SetupChoice'
import { ConnectorAutoConfig } from '../ConnectorAutoConfig/ConnectorAutoConfig'
import { buildAutoConfigAction } from './buildAutoConfigAction'

export const APIConnector = ({
    open,
    onClose,
    stepBack,
    goNext,
    stepConnected,
    selectedInbox,
    setSelectedInbox,
    connectorType,
    handleConnectOnly,
    setKey,
    RecipeCard,
    getMetadata,
    loading,
    showSnackbar,
    setReprocessing,
}) => {
    const userID = useSelector((state) => state.auth.userID)
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const token = useSelector((state) => state.auth.token)
    const [server, setServer] = React.useState(new Server(workspaceID, userID, token))
    const [isConnected, setConnected] = React.useState(false)
    const [objectList, setObjectList] = React.useState([])
    // Object list for the AI-guided flow. For connectors like GA4 the "object"
    // is the Property the user must pick — the AI presets only carry a generic
    // placeholder ("report"), so the AI flow needs the real list to choose from.
    const [autoConfigObjectList, setAutoConfigObjectList] = React.useState([])
    // True while the AI-flow object list is still being fetched. The AI flow
    // must not let the user finish before this resolves, otherwise it would
    // silently fall back to the preset's placeholder object.
    const [autoConfigObjectListLoading, setAutoConfigObjectListLoading] = React.useState(false)
    const [fieldList, setFieldList] = React.useState(null)
    const [gsObjectGroup, setGsObjectGroup] = React.useState(null) //GJ added specifically for GoogleSheets
    const [objectGroupList, setObjectGroupList] = React.useState([])
    const [selectedFieldList, setSelectedFieldList] = React.useState([])
    const [selectedObject, setSelectedObject] = React.useState(null)
    const [connectionKey, setConnectionKey] = React.useState(null)
    const [apiKey, setAPIKey] = React.useState(null)
    const [reportName, setReportName] = React.useState('')
    const [APIFeatures, setAPIFeatures] = React.useState(null)
    const [filterMap, setFilterMap] = React.useState(new Map())
    const [selectedObjectGroup, setSelectedObjectGroup] = React.useState(null)
    const [extractNow, setExtractNow] = React.useState(true)
    const [apiStep, setApiStep] = React.useState('start')
    const [isLoading, setIsLoading] = useState(false)
    const [type] = useState('Transactional')
    const [openPicker, authResponse] = useDrivePicker()
    const [startDate, setStartDate] = React.useState(
        new Date(new Date().getTime() - 30 * 24 * 60 * 60 * 1000)
    )
    const [errorMessage, setErrorMessage] = useState(null)
    const [apiID, setAPIID] = useState(null)
    const [clientID, setClientID] = useState(null)
    const [scopes, setScopes] = useState(null)
    const [isDialogHidden, setIsDialogHidden] = useState(false)
    const [urlParamValues, setUrlParamValues] = useState({})
    const urlParamValuesRef = useRef({})
    const apiStepRef = useRef('start')
    const intervalRef = useRef(null)
    const [selectedFiles, setSelectedFiles] = useState([])
    const [saveLoading, setSaveLoading] = React.useState(false)
    const [creationPolling, setCreationPolling] = useState(false)
    const isEditing = selectedInbox && selectedInbox.extractDefinition

    const handleCloseSuccess = (datasetName) => {
        setSaveLoading(false)
        if (isEditing) {
            setReprocessing()
            showSnackbar && showSnackbar()
            onClose && onClose()
            return
        }
        if (getMetadata) setTimeout(getMetadata, 1000)
        if (goNext) goNext(datasetName || reportName)
        trackEvent(EVENTS.DATASET.CONNECT_INTEGRATION_COMPLETE, {
            api: {
                connector: `${getSourceName()}`,
                result: 'success',
            },
        })
        window.dataLayer?.push({ event: 'api_connection_added' })
    }

    // polling if inbox creation times out
    const {} = useProcessingPolling(
        POLLING_CHECK_ACTIONS.CREATION,
        creationPolling,
        setCreationPolling,
        { inboxName: reportName },
        handleCloseSuccess,
        150, // Max polling attempts (150 * 2 seconds = 5 minutes)
        () => null,
        workspaceID
    )

    const handleOpenPicker = () => {
        setIsDialogHidden(true)
        openPicker({
            clientId: clientID,
            developerKey: apiKey,
            viewId: 'SPREADSHEETS',
            customScopes: [
                'https://www.googleapis.com/auth/drive',
                'https://www.googleapis.com/auth/drive.file',
            ],
            showUploadView: true,
            showUploadFolders: true,
            supportDrives: true,
            multiselect: false,
            callbackFunction: (data) => {
                if (data.action === 'picked') setSelectedFiles(data.docs)
                if (data.action === 'cancel' || data.action === 'picked') setIsDialogHidden(false)
            },
        })
    }

    useEffect(() => {
        if (userID && token && workspaceID) setServer(new Server(workspaceID, userID, token))
    }, [userID, token, workspaceID])

    useEffect(() => {
        urlParamValuesRef.current = urlParamValues
    }, [urlParamValues])

    useEffect(() => {
        apiStepRef.current = apiStep
    }, [apiStep])

    const setFields = (objectList) => {
        if (isEditing && objectList) {
            for (let i = 0; i < objectList.length; i++) {
                if (objectList[i].name === selectedInbox.extractDefinition.object.name) {
                    setSelectedObject(objectList[i])
                    break
                }
            }
            let newSelectedFields = []
            for (let i = 0; i < selectedInbox.extractDefinition.selectedFields.length; i++) {
                newSelectedFields.push(selectedInbox.extractDefinition.selectedFields[i].name)
            }
            setSelectedFieldList(newSelectedFields)
            if (selectedInbox.extractDefinition.filters) {
                for (let i = 0; i < selectedInbox.extractDefinition.filters.length; i++) {
                    filterMap.set(selectedInbox.extractDefinition.filters[i].fieldName, {
                        operator: selectedInbox.extractDefinition.filters[i].operator,
                        values: selectedInbox.extractDefinition.filters[i].values,
                    })
                }
            } else {
                filterMap.clear()
            }
        }
    }

    useEffect(() => {
        if (isEditing && objectList) {
            setReportName(selectedInbox.label)
            setFields(objectList)
        }
    }, [selectedInbox])

    const testConnected = (interval, server) => {
        const postJSON = {
            action: 'getAPIFeatures',
            connectionType: connectorType,
        }

        if (connectorType === 'Monday') {
            // if product accessed via monday.com and forceLogin was passed in, this would be set in localStorage. Otherwise this is irrelevant
            const subdomain = localStorage.getItem('mondayAccount')
            if (subdomain) {
                postJSON.subdomain = subdomain
            }
        }

        // Include any URL parameter values (e.g., shop domain for Shopify)
        Object.entries(urlParamValuesRef.current).forEach(([key, value]) => {
            if (value) postJSON[key] = value
        })

        server.postData(postJSON, (results) => {
            setAPIFeatures(results)
            if (!results.connectionKey) {
                // No connection found
                setConnected(false)
                if (results.urlParameters && !results.oauthLink) {
                    // Connector needs URL parameters before OAuth can proceed
                    setApiStep('urlParameters')
                } else if (results.apiKey) {
                    setApiStep('apiKey')
                } else if (results.oauthLink) {
                    setApiStep('oauthLink')
                }
            } else {
                if (handleConnectOnly) {
                    // This page is only doing the connection, not the object selection so step back
                    stepConnected()
                    setKey(results.connectionKey)
                } else {
                    if (results.hasConfigurationPresets) {
                        // AI-guided configuration available - show setup choice
                        setConnectionKey(results.connectionKey)
                        // Preload the real object list so the AI flow can let
                        // the user pick the actual object (e.g. the GA4
                        // Property) instead of the preset's placeholder.
                        fetchAutoConfigObjectList(results.connectionKey)
                        setApiStep('setupChoice')
                    } else if (results.hasGooglePicker) {
                        setAPIKey(results.apiKey)
                        setAPIID(results.apiID)
                        setClientID(results.clientID)
                        setScopes(results.scopes)
                        setConnectionKey(results.connectionKey)
                        setApiStep('googlePicker')
                    } else if (results.hasObjectGroup) {
                        getAPIObjectGroupList(results.connectionKey, results)
                        setApiStep('objectGroup')
                    } else {
                        getAPIObjectList(results.connectionKey, results)
                        setApiStep('fields')
                    }
                    clearInterval(interval)
                    setConnected(true)
                    setIsLoading(false)
                }
            }
        })
    }

    const getAPIObjectGroupList = (connectionKey, apiFeatures) => {
        server.postData(
            {
                action: 'getAPIObjectGroupList',
                connectorType: connectorType,
                connectionKey: connectionKey,
                workspaceID: workspaceID,
            },
            (results) => {
                console.log('203 ', results)
                if (results.objectGroups) {
                    setConnected(true)
                    setIsLoading(false)
                    let newObjectGroupList = []
                    results.objectGroups.forEach((objectGroup) => {
                        newObjectGroupList.push({
                            id: objectGroup.name,
                            value: objectGroup.label,
                            group: objectGroup,
                        })
                    })
                    setObjectGroupList(newObjectGroupList)
                    setConnectionKey(connectionKey)
                }
            }
        )
    }

    // Lightweight object fetch for the AI-guided flow: only retrieves the
    // object list (e.g. GA4 Properties) without the side effects of
    // getAPIObjectList (which also selects an object and loads its fields).
    const fetchAutoConfigObjectList = (ckey) => {
        setAutoConfigObjectListLoading(true)
        const finishWithEmptyList = () => {
            setAutoConfigObjectList([])
            setAutoConfigObjectListLoading(false)
        }
        server
            .postData(
                {
                    action: 'getAPIObjectList',
                    connectorType: connectorType,
                    connectionKey: ckey,
                    workspaceID: workspaceID,
                },
                (results) => {
                    setAutoConfigObjectList(results?.objects?.length ? results.objects : [])
                    setAutoConfigObjectListLoading(false)
                },
                null,
                // HTTP error: leave the list empty so the AI flow falls back to
                // the preset's placeholder instead of blocking the user forever.
                finishWithEmptyList
            )
            // Network failure: same graceful fallback.
            .catch(finishWithEmptyList)
    }

    const getAPIObjectList = (connectionKey, apiFeatures, objectGroup) => {
        setErrorMessage(null)
        var action = {
            action: 'getAPIObjectList',
            connectorType: connectorType,
            connectionKey: connectionKey,
            workspaceID: workspaceID,
        }
        if (objectGroup) {
            action.objectGroup = objectGroup
        }
        server.postData(action, (results) => {
            if (results.error) {
                setErrorMessage(results.error)
            } else if (results.objects) {
                setConnected(true)
                let newObjectList = []
                results.objects.forEach((object) => {
                    newObjectList.push(object)
                })
                setObjectList(newObjectList)
                setConnectionKey(connectionKey)
                let selectedObject = newObjectList[0]
                if (isEditing) {
                    for (let i = 0; i < newObjectList.length; i++) {
                        if (newObjectList[i].name === selectedInbox.extractDefinition.object.name) {
                            selectedObject = newObjectList[i]
                            break
                        }
                    }
                }
                handleObjectSelect(
                    selectedObject,
                    apiFeatures,
                    objectGroup,
                    connectionKey,
                    newObjectList
                )
            }
        })
    }

    useEffect(() => {
        if (!workspaceID || !token || !userID || !connectorType) return
        const newServer = new Server(workspaceID, userID, token)
        setServer(newServer)
        const interval = setInterval(() => {
            // Pause polling while user is filling in URL parameters
            if (open && apiStepRef.current !== 'urlParameters') {
                testConnected(interval, newServer)
            }
        }, 3000)
        intervalRef.current = interval
        if (open) testConnected(interval, newServer)
        return () => {
            clearInterval(interval)
        }
    }, [open, userID, token, workspaceID, connectorType])

    function handleObjectSelect(object, apiFeatures, objectGroup, ckey, cobjectList) {
        setErrorMessage(null)
        setSelectedObject(object)
        let action = {
            action: 'getAPIObjectFieldList',
            connectorType: connectorType,
            connectionKey: ckey ? ckey : connectionKey,
            workspaceID: workspaceID,
            object: object,
        }
        if (objectGroup || selectedObjectGroup) {
            action.objectGroup = objectGroup ? objectGroup : selectedObjectGroup.group
            // GJ added specifically for GoogleSheets Mar 1
            if (connectorType === 'GoogleSheets' && !gsObjectGroup)
                setGsObjectGroup(action.objectGroup)
        }
        // GJ added specifically for GoogleSheets Mar 3 -- no idea what other APIs' objectGroup is like, but this works for GS
        if (connectorType === 'GoogleSheets' && gsObjectGroup) {
            action.objectGroup = gsObjectGroup
        }

        server.postData(action, (results) => {
            if (results.error) {
                setErrorMessage(results.error)
            } else {
                let newFieldList = []
                let newSelectedFieldList = []
                if (results.fields) {
                    for (let i = 0; i < results.fields.length; i++) {
                        results.fields[i].id = results.fields[i].name
                        results.fields[i].value = results.fields[i].label
                        if (
                            apiFeatures &&
                            apiFeatures.supportsFilters &&
                            results.fields[i].supportsFilters
                        ) {
                            results.fields[i].filter = 'filter'
                        }
                        newFieldList.push(results.fields[i])
                        if (results.fields[i].default) {
                            newSelectedFieldList.push(results.fields[i].name)
                        }
                    }
                    setFieldList(newFieldList)
                }
                if (isEditing) {
                    // Fields already selected
                    setFields(cobjectList ? cobjectList : objectList)
                } else {
                    setSelectedFieldList(newSelectedFieldList)
                }
                setIsLoading(false)
            }
        })
    }

    function handleSaveAPIKey() {
        server.postData(
            {
                action: 'saveAPIKey',
                connectionType: connectorType,
                apiKey: apiKey,
                workspaceID: workspaceID,
            },
            (results) => {
                setAPIKey(apiKey)
            }
        )
    }

    function handleSave(result, editing) {
        let saveFields = []
        for (let i = 0; i < selectedFieldList.length; i++) {
            for (let j = 0; j < fieldList.length; j++) {
                if (fieldList[j].name === selectedFieldList[i]) {
                    var obj = {
                        name: fieldList[j].name,
                        label: fieldList[j].label,
                        type: fieldList[j].type,
                        fieldType: fieldList[j].fieldType,
                    }
                    saveFields.push(obj)
                    break
                }
            }
        }
        let filters = []
        if (filterMap.size > 0) {
            for (let [key, value] of filterMap) {
                filters.push({
                    fieldName: key,
                    operator: value.operator,
                    values: value.values,
                })
            }
        }
        let extractDefinition = {
            connectorType: connectorType,
            object: selectedObject,
            fields: saveFields,
            filters: filters,
        }

        if (selectedObjectGroup) {
            extractDefinition.objectGroup = selectedObjectGroup.group
        }

        // GABE changed Mar 1:  I don't know how selectedObjectGroup.group behaves for non-GoogleSheet; the line below overwrites it correctly if it's a GoogleSheet by overwriting selectedObjectGroup.group which is how the code worked before
        if (connectorType === 'GoogleSheets' && gsObjectGroup) {
            extractDefinition.objectGroup = gsObjectGroup
        }

        if (startDate && APIFeatures.requireDateRange) {
            extractDefinition.startDate = startDate
        }
        let action = {
            connectionKey: connectionKey,
            workspaceID: workspaceID,
            datasetName: reportName,
            extractDefinition: extractDefinition,
            extractNow: extractNow,
        }
        if (selectedInbox) {
            action.snapshot = selectedInbox.isSnapshot === 'Snapshot'
            action.isMostRecent = selectedInbox.isMostRecent
            action.isIncremental = selectedInbox.isIncremental
            action.isMultipleLoads = selectedInbox.isMultipleLoads
        } else {
            action.snapshot = false
            if (selectedObject.syncTable) {
                action.isMostRecent = true
            }
        }
        if (result) {
            action.snapshot = result.isSnapshot === 'Snapshot'
            action.isMostRecent = result.isMostRecent
            action.isMultipleLoads = result.isMultipleLoads
            action.isIncremental = result.isIncremental
        }
        if (selectedInbox && selectedInbox.inboxID) {
            action.action = 'saveAPIInbox'
            action.inboxID = selectedInbox.inboxID
        } else {
            action.action = 'createAPIInbox'
        }
        setSaveLoading(true)
        server.sheetPostData(
            action,
            (results) => {
                setSelectedInbox({ ...selectedInbox })
                if (results.result === 'Success') handleCloseSuccess()
            },
            null,
            () => {
                if (action.action === 'createAPIInbox') setCreationPolling(true)
                if (action.action === 'saveAPIInbox') handleCloseSuccess()
            }
        )
        setErrorMessage(null)
    }

    // Handle AI-guided configuration completion
    const handleAutoConfigComplete = (config) => {
        // config = { objectName, objectLabel, fieldNames, fieldLabels, filters, dateRange, datasetName }

        const action = buildAutoConfigAction(config, {
            connectorType,
            connectionKey,
            workspaceID,
            selectedInbox,
        })
        const isEdit = action.action === 'saveAPIInbox'

        setSaveLoading(true)
        setReportName(config.datasetName)
        server.sheetPostData(
            action,
            (results) => {
                if (isEdit) setSelectedInbox({ ...selectedInbox })
                if (results.result === 'Success') handleCloseSuccess(config.datasetName)
            },
            null,
            () => {
                if (isEdit) handleCloseSuccess(config.datasetName)
                else setCreationPolling(true)
            }
        )
    }

    const getSourceName = () => {
        return SOURCES_OPTIONS.find((s) => s.sourceValue === connectorType)?.name || ''
    }

    let objectName = 'Object'
    let hasObjectGroup = false
    let objectGroupName = 'Group'
    if (APIFeatures) {
        objectName = APIFeatures.objectName ? APIFeatures.objectName : objectName
        if (APIFeatures.hasObjectGroup) {
            hasObjectGroup = true
            objectGroupName = APIFeatures.objectGroupName
                ? APIFeatures.objectGroupName
                : objectGroupName
        }
    }

    const handleCloseModals = (e, reason) => {
        if (reason && reason === 'backdropClick') return
        onClose()
    }

    const renderGooglePicker = () => {
        return (
            <GooglePickerDialog
                open={open}
                isDialogHidden={isDialogHidden}
                selectedFiles={selectedFiles}
                APIFeatures={APIFeatures}
                objectGroupName={objectGroupName}
                errorMessage={errorMessage}
                onClose={handleCloseModals}
                onBack={stepBack}
                onOpenPicker={handleOpenPicker}
                onNext={() => {
                    setSelectedObjectGroup(selectedFiles[0].id)
                    getAPIObjectList(connectionKey, APIFeatures, {
                        name: selectedFiles[0].id,
                        label: selectedFiles[0].name,
                    })
                    setApiStep('fields')
                }}
                onDeleteFile={(index) => {
                    setSelectedFiles(selectedFiles.filter((_, i) => i !== index))
                }}
            />
        )
    }

    const renderObjectGroup = () => {
        return (
            <ScoopDialog
                open={open}
                title={
                    'Dataset from ' +
                    displayPorperProductNameInDialog(APIFeatures?.connectorName) +
                    ' ' +
                    objectGroupName
                }
                onClose={handleCloseModals}
                actions={
                    <>
                        <ScoopButton variant="text" onClick={stepBack}>
                            Back
                        </ScoopButton>
                    </>
                }
                maxWidth={1200}
            >
                <div className={styles.dialogContentPadding}>
                    <ScoopText className={styles.textMarginTop}>
                        Select the {objectGroupName} you would like to retrieve
                    </ScoopText>
                    {errorMessage && (
                        <ScoopText className={styles.errorMessage}>{errorMessage}</ScoopText>
                    )}
                    <div className={styles.dataGridContainer}>
                        <DataGrid
                            autoHeight
                            columns={[
                                {
                                    field: 'value',
                                    headerName: 'Value',
                                    type: 'string',
                                    sortable: true,
                                    editable: false,
                                    width: 600,
                                    headerAlign: 'center',
                                },
                            ]}
                            pageSizeOptions={[15]}
                            initialState={{
                                pagination: {
                                    paginationModel: { page: 0, pageSize: 15 },
                                },
                                sorting: {
                                    sortModel: [{ field: 'value', sort: 'asc' }],
                                },
                            }}
                            rows={objectGroupList}
                            rowHeight={25}
                            onRowClick={(event) => {
                                setSelectedObjectGroup(event.row)
                                getAPIObjectList(connectionKey, APIFeatures, event.row.group)
                                setApiStep('fields')
                            }}
                        />
                    </div>
                </div>
            </ScoopDialog>
        )
    }

    const renderAPIKey = () => {
        return (
            <ScoopDialog
                open={open}
                title={
                    'Dataset from ' +
                    displayPorperProductNameInDialog(APIFeatures?.connectorName) +
                    ' ' +
                    objectName
                }
                onClose={handleCloseModals}
                actions={
                    <>
                        <ScoopButton variant="text" onClick={stepBack}>
                            Back
                        </ScoopButton>
                        <ScoopButton onClick={handleSaveAPIKey}>Save Key</ScoopButton>
                    </>
                }
                className={styles.dialogWidth500}
            >
                <div className={styles.dialogContentWithRecipe}>
                    {APIFeatures && (
                        <>
                            <ScoopText>Enter your API Key to connect to {connectorType}</ScoopText>
                            <ScoopTextField
                                className={styles.inputWidth300}
                                value={apiKey}
                                onChange={(event) => {
                                    setAPIKey(event.target.value)
                                }}
                            />
                        </>
                    )}
                    {!APIFeatures && (
                        <ScoopText className={styles.noApiText}>No API Found</ScoopText>
                    )}
                </div>
            </ScoopDialog>
        )
    }

    const renderOauthLink = () => {
        return (
            <ScoopDialog
                className={RecipeCard ? styles.oauthDialogWide : styles.oauthDialogNarrow}
                maxWidth={RecipeCard ? 'lg' : 'md'}
                open={open}
                title={`Dataset from ${APIFeatures?.connectorName ? displayPorperProductNameInDialog(APIFeatures.connectorName) : getSourceName() || ''}`}
                onClose={handleCloseModals}
                actions={
                    <>
                        <ScoopButton variant="text" onClick={stepBack}>
                            Back
                        </ScoopButton>
                        {APIFeatures &&
                            APIFeatures.oauthLink &&
                            displayProperProductButton(
                                APIFeatures.connectorName
                                    ? APIFeatures.connectorName
                                    : connectorType,
                                APIFeatures
                            )}
                    </>
                }
            >
                <div className={RecipeCard ? styles.dialogContentWithRecipe : styles.dialogContent}>
                    {loading || !APIFeatures ? (
                        <div className={styles.loadingContainer}>
                            <ScoopSpinner />
                        </div>
                    ) : (
                        <>
                            {APIFeatures ? (
                                <>
                                    {RecipeCard ? (
                                        RecipeCard
                                    ) : (
                                        <>
                                            <ScoopText className={styles.text}>
                                                Click "Next" to connect to{' '}
                                                {APIFeatures?.connectorName
                                                    ? displayPorperProductNameInDialog(
                                                          APIFeatures.connectorName
                                                      )
                                                    : getSourceName()}
                                                .
                                            </ScoopText>
                                            <div className={styles.socBadge}>
                                                <ScoopText className={styles.socText}>
                                                    Our platform follows SOC 2 Type II standards for
                                                    secure and reliable handling of your data.
                                                </ScoopText>
                                                <img
                                                    className={styles.socImage}
                                                    alt="SOC"
                                                    src={SOC}
                                                />
                                            </div>
                                        </>
                                    )}
                                </>
                            ) : (
                                <ScoopText className={styles.noApiText}>No API Found</ScoopText>
                            )}
                        </>
                    )}
                </div>
            </ScoopDialog>
        )
    }

    const renderLoading = () => {
        const displayName = APIFeatures?.connectorName
            ? displayPorperProductNameInDialog(APIFeatures.connectorName)
            : getSourceName()
        return (
            <ScoopDialog
                open={open}
                title={displayName ? `Dataset from ${displayName}` : 'Connecting...'}
                onClose={handleCloseModals}
                actions={
                    <>
                        <ScoopButton variant="text" onClick={stepBack}>
                            Back
                        </ScoopButton>
                    </>
                }
                className={styles.dialogWidth500}
            >
                <div className={styles.loadingDialogContent}>
                    <ScoopSpinner></ScoopSpinner>
                </div>
            </ScoopDialog>
        )
    }

    const renderFields = () => {
        // Show loading state when saving
        if (saveLoading) {
            return (
                <ScoopDialog
                    open={open}
                    title={
                        'Dataset from ' +
                        displayPorperProductNameInDialog(APIFeatures?.connectorName) +
                        ' ' +
                        objectName
                    }
                    onClose={handleCloseModals}
                    className={styles.dialogWidth500}
                >
                    <div className={styles.savingContainer}>
                        <ScoopSpinner size={40} />
                        <ScoopText className={styles.savingTitle}>
                            {`${isEditing ? 'Editing' : 'Creating'} your data source...`}
                        </ScoopText>
                        <ScoopText className={styles.savingSubtitle}>
                            Please wait while we extract and prepare your data
                        </ScoopText>
                    </div>
                </ScoopDialog>
            )
        }

        let nextButton = 'Create Data Source'
        let next = () => {
            handleSave(null, isEditing)
        }
        if (isEditing) {
            nextButton = 'Save Data Source'
        }
        return (
            <APIFields
                saveLoading={saveLoading}
                dialogOpen={open}
                APIFeatures={APIFeatures}
                stepBack={stepBack}
                onClose={onClose}
                selectedFieldList={selectedFieldList}
                selectedInbox={selectedInbox}
                objectList={objectList}
                fieldList={fieldList}
                filterMap={filterMap}
                reportName={reportName}
                selectedObject={selectedObject}
                setReportName={setReportName}
                setSelectedFieldList={setSelectedFieldList}
                extractNow={extractNow}
                setExtractNow={setExtractNow}
                handleObjectSelect={handleObjectSelect}
                nextButton={nextButton}
                next={next}
                startDate={startDate}
                setStartDate={setStartDate}
                errorMessage={errorMessage}
                server={server}
                connectorType={connectorType}
                connectionKey={connectionKey}
                workspaceID={workspaceID}
            />
        )
    }

    const renderSetupChoice = () => {
        return (
            <SetupChoice
                open={open}
                connectorName={APIFeatures?.connectorName || connectorType}
                onSelectAI={() => setApiStep('autoConfig')}
                onSelectManual={() => {
                    if (APIFeatures?.hasObjectGroup) {
                        getAPIObjectGroupList(connectionKey, APIFeatures)
                        setApiStep('objectGroup')
                    } else {
                        getAPIObjectList(connectionKey, APIFeatures)
                        setApiStep('fields')
                    }
                }}
                onClose={handleCloseModals}
                onBack={stepBack}
            />
        )
    }

    const renderAutoConfig = () => {
        return (
            <ConnectorAutoConfig
                open={open}
                connectorType={connectorType}
                connectorName={APIFeatures?.connectorName || connectorType}
                connectionKey={connectionKey}
                workspaceID={workspaceID}
                requireDateRange={APIFeatures?.requireDateRange}
                objectList={autoConfigObjectList}
                objectListLoading={autoConfigObjectListLoading}
                objectSelectionLabel={APIFeatures?.objectName}
                onComplete={handleAutoConfigComplete}
                onCancel={() => setApiStep('setupChoice')}
                onClose={handleCloseModals}
                onFallbackToManual={() => {
                    if (APIFeatures?.hasObjectGroup) {
                        getAPIObjectGroupList(connectionKey, APIFeatures)
                        setApiStep('objectGroup')
                    } else {
                        getAPIObjectList(connectionKey, APIFeatures)
                        setApiStep('fields')
                    }
                }}
            />
        )
    }

    const renderUrlParameters = () => {
        const params = APIFeatures?.urlParameters || []
        return (
            <ScoopDialog
                open={open}
                title={`Connect to ${displayPorperProductNameInDialog(APIFeatures?.connectorName || connectorType)}`}
                onClose={handleCloseModals}
                actions={
                    <>
                        <ScoopButton variant="text" onClick={stepBack}>
                            Back
                        </ScoopButton>
                        <ScoopButton
                            onClick={() => {
                                setApiStep('oauthLink')
                                testConnected(intervalRef.current, server)
                            }}
                            disabled={params.some((p) => {
                                const tokenName = p.token.replace('{', '').replace('}', '')
                                return !urlParamValues[tokenName]
                            })}
                        >
                            Next
                        </ScoopButton>
                    </>
                }
                className={styles.oauthDialogWide}
            >
                <div className={styles.dialogContentPadding}>
                    {params.map((param) => {
                        const tokenName = param.token.replace('{', '').replace('}', '')
                        return (
                            <div key={tokenName} className={styles.urlParamField}>
                                <ScoopText>{param.label}</ScoopText>
                                <div className={styles.urlParamInputRow}>
                                    <ScoopTextField
                                        containerClassName={styles.urlParamInput}
                                        placeholder={param.placeholder || ''}
                                        value={urlParamValues[tokenName] || ''}
                                        onChange={(e) =>
                                            setUrlParamValues((prev) => ({
                                                ...prev,
                                                [tokenName]: e.target.value,
                                            }))
                                        }
                                    />
                                    {param.suffix && <ScoopText>{param.suffix}</ScoopText>}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </ScoopDialog>
        )
    }

    const renderStepContent = () => {
        switch (apiStep) {
            case 'start':
                if (RecipeCard) return renderOauthLink()
                return renderLoading()
            case 'setupChoice':
                return renderSetupChoice()
            case 'autoConfig':
                return renderAutoConfig()
            case 'objectGroup':
                return renderObjectGroup()
            case 'googlePicker':
                return renderGooglePicker()
            case 'fields':
                return renderFields()
            case 'urlParameters':
                return renderUrlParameters()
            case 'apiKey':
                return renderAPIKey()
            case 'oauthLink':
                return renderOauthLink()
            default:
                return renderFields()
        }
    }

    return (
        <>
            {isLoading && renderLoading()}
            {!isLoading && renderStepContent()}
        </>
    )
}
